import styles from './AssistantPanel.module.css';

import type { FC } from 'react';
import type { ApiAssistantSaleCar, ApiAssistantSaleEvidence, ApiAssistantSuggestedAction } from 'src/types/assistant';

import React from 'react';
import { Link } from 'react-router';
import classNames from 'classnames';

import { formatMoney, formatAmount } from 'src/utils/money';
import { fuelLabel, gearboxLabel, bodyTypeLabel } from 'src/utils/carAttributes';

import { Typography } from 'src/components/themed/Typography';

import { ListingAvailability } from 'src/types/listing';

import { CarChecks } from './CarChecks';

/** The tone of a line: what the evidence says of the car, at a glance. */
type Tone = 'good' | 'bad' | 'muted' | 'plain';

interface Line {
  label: string;
  text: string;
  tone: Tone;
}

const plural = (count: number, one: string, many: string): string => `${count} ${count === 1 ? one : many}`;

/** What the listing states, as the dealer wrote it — every field only when stated, and "not stated" where it matters. */
export const listedLine = (listed: ApiAssistantSaleEvidence['listed']): Line => {
  const parts = [
    listed.year != null ? String(listed.year) : 'year not stated',
    listed.mileage != null ? `${formatAmount(listed.mileage)} km` : 'mileage not stated',
    bodyTypeLabel(listed.bodyType),
    gearboxLabel(listed.gearbox),
    fuelLabel(listed.fuel),
    listed.color ?? undefined,
    plural(listed.photos, 'photo', 'photos'),
    listed.descriptionStated ? 'description by the dealer' : 'no description',
  ].filter((part): part is string => !!part);

  return { label: 'Listed', text: parts.join(' · '), tone: 'plain' };
};

/**
 * The model year the VIN gives beside the listed one (api#440): a model year one above the listed
 * year is normal — a car is often built and sold the year before — and said so, so a "2026" beside a
 * listed 2025 does not read as a contradiction. Nothing when the two are the same, or no year is known.
 */
const modelYearWords = (vin: ApiAssistantSaleEvidence['vin'], listedYear: number | null | undefined): string => {
  const year = vin.decoded?.year ?? vin.modelYear ?? null;

  if (year == null || listedYear == null) {
    return '';
  }

  if (year === listedYear) {
    return vin.decoded?.year == null ? `; model year ${year} by the VIN` : '';
  }

  return `; model year ${year}${vin.decoded?.year == null ? ' by the VIN' : ''} for a car listed as ${listedYear}, which is normal`;
};

/** The VIN against the listing: decoded facts and where they agree or not (api#425, decision 1). */
export const vinLine = (vin: ApiAssistantSaleEvidence['vin'], listedYear?: number | null): Line => {
  if (vin.status === 'not_stated') {
    return { label: 'VIN', text: 'Not stated by the dealer: a minus', tone: 'bad' };
  }

  if (vin.status === 'malformed') {
    return { label: 'VIN', text: 'Stated, but not a valid VIN: a minus', tone: 'bad' };
  }

  const decoded = vin.decoded
    ? [vin.decoded.make, vin.decoded.model, vin.decoded.year != null ? String(vin.decoded.year) : null]
        .filter((part): part is string => !!part)
        .join(' ')
    : '';
  const built = vin.decoded?.builtIn ? `, built in ${vin.decoded.builtIn}` : '';
  const year = modelYearWords(vin, listedYear);

  switch (vin.check) {
    case 'matches':
      return { label: 'VIN', text: `Decodes to ${decoded || 'the listed make'}${built}: matches the listing${year}`, tone: 'good' };
    case 'differs':
      return {
        label: 'VIN',
        text: `Decodes to ${decoded}${built}: differs from the listing in ${vin.differences.join(' and ')}`,
        tone: 'bad',
      };
    case 'unknown':
      return {
        label: 'VIN',
        text: `Stated; the decoder has no record of it${vin.decoded?.manufacturer ? ` (${vin.decoded.manufacturer})` : ''}${year}`,
        tone: 'muted',
      };
    case 'unavailable':
      return { label: 'VIN', text: `Stated; could not be checked right now${year}`, tone: 'muted' };
    default:
      return { label: 'VIN', text: `Stated${year}`, tone: 'plain' };
  }
};

const positionWords = (price: ApiAssistantSaleEvidence['price']): string => {
  const percent = price.fromMedianPercent == null ? '' : `${Math.abs(price.fromMedianPercent)}%`;

  switch (price.position) {
    case 'lowest':
      return `the lowest price${percent ? `, ${percent} below the median` : ''}`;
    case 'highest':
      return `the highest price${percent ? `, ${percent} above the median` : ''}`;
    case 'below_median':
      return `${percent} below the median`;
    case 'above_median':
      return `${percent} above the median`;
    case 'at_median':
      return 'at the median';
    default:
      return '';
  }
};

/** This car's price against the similar cars of this catalogue — the numbers the platform computed, nothing external. */
export const priceLine = (price: ApiAssistantSaleEvidence['price'], title: string): Line => {
  if (price.similar === 0 || price.min == null || price.max == null || price.median == null) {
    return { label: 'Price vs similar', text: `No other ${title} in this catalogue to compare with`, tone: 'muted' };
  }

  const range =
    price.min === price.max
      ? formatMoney(price.min, price.currency)
      : `${formatAmount(price.min)}–${formatMoney(price.max, price.currency)}`;
  const mileage = price.mileageOfSimilar
    ? `; their mileage ${formatAmount(price.mileageOfSimilar.min)}–${formatAmount(price.mileageOfSimilar.max)} km`
    : '';
  const tone: Tone = price.position === 'lowest' || price.position === 'below_median' ? 'good' : 'plain';

  return {
    label: 'Price vs similar',
    text: `${plural(price.similar, 'similar car', 'similar cars')} from ${plural(price.dealers, 'dealer', 'dealers')} in this catalogue: ${range}, median ${formatMoney(price.median, price.currency)}; this one is ${positionWords(price)}${mileage}`,
    tone,
  };
};

const chainName = (chainKey: string | null): string => (chainKey ? chainKey.charAt(0).toUpperCase() + chainKey.slice(1) : '');

/** How the car can be bought: the escrow with delivery or Buy Now, and what settles it. */
export const purchaseLine = (purchase: ApiAssistantSaleEvidence['purchase']): Line => {
  if (purchase.availability === ListingAvailability.Sold) {
    return { label: 'How to buy', text: 'Sold', tone: 'bad' };
  }

  if (purchase.availability === ListingAvailability.Reserved) {
    return { label: 'How to buy', text: 'Reserved: a Buy Now purchase is being verified', tone: 'muted' };
  }

  if (!purchase.buyable) {
    return { label: 'How to buy', text: 'Cannot be bought right now: the dealer has no payout wallet for it', tone: 'bad' };
  }

  const settles = purchase.tokenSymbol ? ` · paid in ${purchase.tokenSymbol}${purchase.chainKey ? ` on ${chainName(purchase.chainKey)}` : ''}` : '';

  return { label: 'How to buy', text: `Escrow with delivery (the dealer is paid when the logist picks the car up) or Buy Now${settles}`, tone: 'plain' };
};

/**
 * The price with the platform fee of the escrow with delivery (api#430, Tim: "only when it is a
 * purchase"): the platform's numbers; the delivery the dealer names in reply to the offer, to the
 * city the person named if any. Buy Now has neither fee nor delivery. Nothing for a car that cannot
 * be bought now, or on a card from an api before api#430.
 */
export const deliveredLine = (
  delivered: ApiAssistantSaleEvidence['delivered'],
  deliveryCity: string | null | undefined,
): Line | null => {
  if (!delivered) {
    return null;
  }

  const to = deliveryCity ? `delivery to ${deliveryCity}` : 'delivery to your city';

  return {
    label: 'Delivered price',
    text:
      `${formatMoney(delivered.price, delivered.currency)} + platform fee ${formatMoney(delivered.platformFee, delivered.currency)} = ` +
      `${formatMoney(delivered.total, delivered.currency)}; ${to}: the dealer names it in reply to your offer · Buy Now: the price, no fee, no delivery`,
    tone: 'plain',
  };
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `2026-07` → "Jul 2026", the same on every device: no locale, no time zone. */
const monthWords = (month: string | null): string | undefined => {
  const match = month ? /^(\d{4})-(\d{2})$/.exec(month) : null;
  const name = match ? MONTHS[Number(match[2]) - 1] : undefined;

  return match && name ? `${name} ${match[1]}` : undefined;
};

/** The non-zero counts of a group, in the given order: `[[7, 'handed over'], [0, 'in progress']]` → "7 handed over". */
const counts = (entries: [number, string][]): string =>
  entries
    .filter(([count]) => count > 0)
    .map(([count, words]) => `${count} ${words}`)
    .join(', ');

const total = (group: Record<string, number>): number => Object.values(group).reduce((sum, count) => sum + count, 0);

/**
 * One kind of deal: its counts, or "no deals yet" — a fact on a young platform, not a verdict. A
 * count under a name this storefront does not know yet (a later api's) says nothing rather than
 * leave the kind's name bare.
 */
const kindOfDeal = (name: string, group: Record<string, number>, text: () => string): string =>
  `${name}: ${(total(group) > 0 && text()) || 'no deals yet'}`;

/**
 * The dealer's record on this platform (api#434, the spec §2а; the wording Tim's choice of
 * 2026-10-27): analytics the platform counted, no rating. The deliveries that did not go through are
 * grouped by who stopped them — a deal the buyer stopped is not the dealer's fault. Null: a card
 * from an api before api#434, which has no record to show.
 */
export const dealerLine = (dealer: ApiAssistantSaleEvidence['dealer']): Line | null => {
  if (dealer === undefined) {
    return null;
  }

  if (dealer === null) {
    return { label: 'Dealer', text: 'The dealer’s record could not be read right now', tone: 'muted' };
  }

  const since = monthWords(dealer.since);
  const { delivery, buyNow, rental } = dealer;
  const deliveries = kindOfDeal('Deliveries', delivery, () =>
    [
      counts([
        [delivery.handedOver, 'handed over'],
        [delivery.inProgress, 'in progress'],
        [delivery.inDispute, 'in dispute'],
      ]),
      [delivery.withdrawnByBuyer, delivery.cancelledByBuyer, delivery.notPaidInTime].some((count) => count > 0)
        ? `stopped by the buyer: ${counts([
            [delivery.withdrawnByBuyer, 'withdrawn'],
            [delivery.cancelledByBuyer, 'cancelled'],
            [delivery.notPaidInTime, 'not paid in time'],
          ])}`
        : '',
      delivery.declinedByDealer > 0 ? `declined by the dealer: ${delivery.declinedByDealer}` : '',
      delivery.offersExpired > 0 ? `offers expired: ${delivery.offersExpired}` : '',
      delivery.refundedAfterDispute > 0 ? `refunded after a dispute: ${delivery.refundedAfterDispute}` : '',
    ]
      .filter((group) => !!group)
      .join('; ')
  );
  const purchases = kindOfDeal('Buy Now', buyNow, () =>
    counts([
      [buyNow.paid, 'paid'],
      [buyNow.beingVerified, 'being verified'],
      [buyNow.notConfirmed, buyNow.notConfirmed === 1 ? 'payment not confirmed' : 'payments not confirmed'],
    ])
  );
  const rentals = kindOfDeal('Rentals', rental, () =>
    counts([
      [rental.completed, 'completed'],
      [rental.inProgress, 'in progress'],
      [rental.inDispute, 'in dispute'],
      [rental.cancelledByRenter, 'cancelled by the renter'],
    ])
  );
  const cars = `${dealer.carsForSale > 0 ? plural(dealer.carsForSale, 'car', 'cars') : 'no cars'} for sale, ${
    dealer.carsForRent > 0 ? dealer.carsForRent : 'none'
  } for rent`;

  return {
    label: 'Dealer',
    text: [since ? `On the platform since ${since}` : undefined, cars, deliveries, purchases, rentals]
      .filter((part): part is string => !!part)
      .join(' · '),
    tone: 'plain',
  };
};

/** The sale card's second line: the mileage, the gearbox and the fuel the listing states, like the catalog's card. */
export const saleSecondaryLine = (car: ApiAssistantSaleCar): string | undefined => {
  const parts = [
    car.mileage != null ? `${formatAmount(car.mileage)} km` : undefined,
    gearboxLabel(car.evidence?.listed.gearbox),
    fuelLabel(car.evidence?.listed.fuel),
  ].filter((part): part is string => !!part);

  return parts.length > 0 ? parts.join(' · ') : undefined;
};

interface EvidenceCardProps {
  car: ApiAssistantSaleCar;
  /** The reply's action: the city of "Prepare the offer", when the person named one (api#430). */
  action?: ApiAssistantSuggestedAction;
}

/**
 * The evidence card under a car for sale (api#425, the spec §2): what the listing states, the VIN
 * against the listing, the price against the similar cars of this catalogue, how it can be bought,
 * the dealer's record on the platform (api#434) — every number the platform's, drawn here as five
 * short lines — and the way to the dealer's other cars. The assistant reads the same record.
 */
export const EvidenceCard: FC<EvidenceCardProps> = ({ car, action }) => {
  if (!car.evidence) {
    return null;
  }

  const delivered = deliveredLine(car.evidence.delivered, action?.type === 'prepareOffer' ? action.deliveryCity : null);
  const lines = [
    listedLine(car.evidence.listed),
    vinLine(car.evidence.vin, car.evidence.listed.year),
    priceLine(car.evidence.price, car.title.replace(/\s\d{4}$/, '')),
    purchaseLine(car.evidence.purchase),
    ...(delivered ? [delivered] : []),
  ];
  const dealer = dealerLine(car.evidence.dealer);

  return (
    <>
    <dl className={styles.evidence} aria-label={`Evidence: ${car.title}`}>
      {lines.map((line) => (
        <div key={line.label} className={classNames(styles.evidenceRow, styles[`tone-${line.tone}`])}>
          <dt>
            <Typography as="span" color="lightWhite" variant="small" fontWeight="600">
              {line.label}
            </Typography>
          </dt>
          <dd>
            <Typography as="span" color="white" variant="small">
              {line.text}
            </Typography>
          </dd>
        </div>
      ))}
      <div className={classNames(styles.evidenceRow, dealer && styles[`tone-${dealer.tone}`])}>
        <dt>
          <Typography as="span" color="lightWhite" variant="small" fontWeight="600">
            {dealer?.label ?? 'Dealer'}
          </Typography>
        </dt>
        <dd>
          {dealer && (
            <Typography as="span" color="white" variant="small" className={styles.dealerRecord}>
              {dealer.text}
            </Typography>
          )}
          <Link to={`/dealer/${encodeURIComponent(car.sellerId)}`} className={styles.dealerLink}>
            <Typography as="span" color="white" variant="small">
              All cars by this dealer
            </Typography>
          </Link>
        </dd>
      </div>
    </dl>
    {car.evidence.checks ? <CarChecks checks={car.evidence.checks} vinStatus={car.evidence.vin.status} title={car.title} /> : null}
    </>
  );
};
